/**
 * Passages abonnement — planification (LOT 1).
 * Ancien module « Descentes » reste disponible séparément ; ici la chaîne Contrat → Passage → Tournée.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { CalendarDays, ChevronRight, RefreshCw } from "lucide-react";
import { api } from "../../../lib/axios";
import { useAuth } from "../../../context/AuthContext";
import { ClientDemandesAdminPanel } from "../../../components/planificateur/ClientDemandesAdminPanel";
import type { PassageApiItem } from "./planningTypes";
import { passageStatutLecture, passageStatutLectureBadgeClass } from "./planningDashboardUtils";
import {
  OPERATIONAL_FILTER_ALL_LABEL_FR,
  OPERATIONAL_PASSAGE_STATUS_VALUES,
  OPERATIONAL_STATUS_META,
  type OperationalPassageStatus,
} from "@/domain/planning/operationalPassageStatus";
import {
  TOURNEE_OPERATIONAL_MODE_OPTIONS,
  TourneeOperationalMode,
  tourneeModeAllowsOptionalFleetVehicle,
  tourneeModeRequiresRollingUnit,
  tourneeOperationalModeLabelFr,
} from "@/domain/planning/tourneeOperationalMode";

type SequenceGuard = {
  blocked: boolean;
  blockingPassageId?: number;
  blockingDate?: string;
  blockingStatus?: string;
  message?: string;
};

type PassageRow = {
  id: number;
  contratId: number;
  clientId: number | null;
  datePrevue: string;
  statut: string;
  dateRealisee?: string | null;
  executionStatus?: string | null;
  passageType?: string | null;
  isUrgent?: boolean;
  isFacturable: boolean;
  ordreDansTournee: number | null;
  client?: { id: number; nom?: string | null; prenom?: string | null; nomEntreprise?: string | null } | null;
  contrat?: { id: number; clientId?: number };
  zone?: { id: number; nom: string } | null;
  zoneId?: number | null;
  tourneeId?: number | null;
  tournee?: {
    id: number;
    numero: string | null;
    statut: string;
    executionStatus?: string | null;
    closedAt?: string | null;
  } | null;
  displayStatus?: string | null;
  displayLabel?: string | null;
  adminStatusLabel?: string | null;
  adminStatusTone?: string | null;
  operationalStatus?: OperationalPassageStatus | string | null;
  operationalStatusLabel?: string | null;
  operationalStatusTone?: string | null;
  adminActionLabel?: string | null;
  adminPriorityWeight?: number | null;
  operationalBadges?: string[] | null;
  operationallyOverdue?: boolean;
  mission?: { id: number; numero?: string | null; statut?: string } | null;
  equipeId?: number | null;
  teamAssignmentStatus?: string | null;
  teamAssignmentStatusResolved?: string | null;
  teamAssignment?: TeamAssignmentState | null;
  adminActionLabels?: string[] | null;
  sequenceGuard?: SequenceGuard | null;
  sequenceBlocked?: boolean;
  provisionAssignmentBlocked?: boolean;
  provisionBlockMessage?: string | null;
  /** Exclu de la vue planification opérationnelle (terrain / validation terminés). */
  passageProcessed?: boolean;
  operationalBucket?:
    | "TO_ASSIGN"
    | "TO_CONFIRM"
    | "CONFIRMED_NEEDS_TOURNEE"
    | "TOURNEE_ASSIGNED"
    | "IN_EXECUTION"
    | "BLOCKED_PROVISION"
    | "COMPLETED"
    | string
    | null;
  operationalBucketLabel?: string | null;
};

type WorkflowTab = "A_AFFECTER" | "TODAY" | "WEEK" | "ALL";
type ZoneOption = { id: number; nom: string };
type TeamOption = { id: number; nom: string };
type VehiculeOption = { id: number; immatriculation?: string | null };
type CompatibleTournee = {
  id: number;
  date: string;
  zoneLabel: string | null;
  equipeLabel: string | null;
  vehiculeLabel: string | null;
  statut: string;
  passagesCount: number;
  capaciteRestante: number | null;
};

type OperationalModeAvailability = {
  mode: TourneeOperationalMode | string;
  required: boolean;
  total: number;
  used: number;
  available: number;
  isAvailable: boolean;
  message: string | null;
  units: Array<{
    id: number;
    reference: string;
    label: string;
    catalogName: string;
    family: string | null;
    plateNumber: string | null;
  }>;
};

type TeamAssignmentState = {
  status?: string;
  effectiveStatus?: "NONE" | "PROPOSED" | "CONFIRMED" | "TOURNEE" | "IN_PROGRESS" | "TERMINAL";
  label?: string;
  actionLabels?: string[];
};

function isPassageSequenceBlocked(p: PassageRow): boolean {
  return Boolean(p.sequenceGuard?.blocked ?? p.sequenceBlocked);
}

function isPassageProvisionBlocked(p: PassageRow): boolean {
  return Boolean(p.provisionAssignmentBlocked);
}

function isPassageTerrainAssignBlocked(p: PassageRow): boolean {
  return isPassageSequenceBlocked(p) || isPassageProvisionBlocked(p);
}

function teamAssignmentEffectiveStatus(p: PassageRow): TeamAssignmentState["effectiveStatus"] {
  if (p.teamAssignment?.effectiveStatus) return p.teamAssignment.effectiveStatus;
  if (p.passageProcessed) return "TERMINAL";
  if (p.tournee?.executionStatus === "EN_COURS" || p.executionStatus === "EN_COURS") return "IN_PROGRESS";
  if (p.tourneeId != null || p.tournee?.id != null) return "TOURNEE";
  if (p.teamAssignmentStatusResolved === "CONFIRMED" || p.teamAssignmentStatus === "CONFIRMED") return "CONFIRMED";
  if (p.equipeId != null) return "PROPOSED";
  return "NONE";
}

function isTeamAssignmentConfirmed(p: PassageRow): boolean {
  return teamAssignmentEffectiveStatus(p) === "CONFIRMED";
}

function passageSequenceTooltip(p: PassageRow): string | undefined {
  const g = p.sequenceGuard;
  if (!isPassageSequenceBlocked(p) || !g?.blockingDate) return undefined;
  return `Passage antérieur à traiter : ${new Date(g.blockingDate).toLocaleDateString("fr-FR")}`;
}

export default function PlanningPassagesPage(): React.JSX.Element {
  const { user } = useAuth();
  const canForceSequenceAssign = useMemo(() => {
    const r = String(user?.role ?? "")
      .toUpperCase()
      .trim();
    return r === "ADMIN" || r === "SUPERVISEUR";
  }, [user?.role]);

  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [tab, setTab] = useState<WorkflowTab>("A_AFFECTER");
  const [date, setDate] = useState(todayIso);
  const [zoneId, setZoneId] = useState("");
  const [operationalStatus, setOperationalStatus] = useState("");
  const [includeBlocked, setIncludeBlocked] = useState(false);
  const [blockedHiddenCount, setBlockedHiddenCount] = useState(0);
  const [clientId, setClientId] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const clientFiltersActive = useMemo(
    () => clientId.trim() !== "" || clientSearch.trim() !== "",
    [clientId, clientSearch]
  );
  const [items, setItems] = useState<PassageRow[]>([]);
  const [affecterItems, setAffecterItems] = useState<PassageRow[]>([]);
  const [viewBadge, setViewBadge] = useState("Vue opérationnelle");
  const [viewSubtitle, setViewSubtitle] = useState(
    "Vue opérationnelle — prochains passages à traiter (urgences, retards, sans tournée, fenêtre courte)."
  );
  const [loading, setLoading] = useState(false);
  const [equipesMap, setEquipesMap] = useState<Record<number, string>>({});
  const [zones, setZones] = useState<ZoneOption[]>([]);
  const [equipes, setEquipes] = useState<TeamOption[]>([]);
  const [vehicules, setVehicules] = useState<VehiculeOption[]>([]);
  const [selectedPassage, setSelectedPassage] = useState<PassageRow | null>(null);
  const [compatibles, setCompatibles] = useState<CompatibleTournee[]>([]);
  const [selectedCompatibleTourneeId, setSelectedCompatibleTourneeId] = useState<number | null>(null);
  const [assignLoading, setAssignLoading] = useState(false);
  const [createDate, setCreateDate] = useState("");
  const [createZoneId, setCreateZoneId] = useState("");
  const [showZoneEditor, setShowZoneEditor] = useState(false);
  const [createEquipeId, setCreateEquipeId] = useState("");
  const [createVehiculeId, setCreateVehiculeId] = useState("");
  const [modeOperationnel, setModeOperationnel] = useState<TourneeOperationalMode>(TourneeOperationalMode.WALKING);
  const [createResourceUnitId, setCreateResourceUnitId] = useState("");
  const [modeAvailability, setModeAvailability] = useState<OperationalModeAvailability | null>(null);
  const [modeAvailabilityLoading, setModeAvailabilityLoading] = useState(false);
  const [createComment, setCreateComment] = useState("");

  const [genContratId, setGenContratId] = useState("");
  const [genDebut, setGenDebut] = useState(todayIso);
  const [genFin, setGenFin] = useState(todayIso);

  const [pendingSequenceOverrideReason, setPendingSequenceOverrideReason] = useState<string | null>(null);
  const [forceSequencePassage, setForceSequencePassage] = useState<PassageRow | null>(null);
  const [forceSequenceReasonDraft, setForceSequenceReasonDraft] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const usesClient = clientId.trim() !== "" || clientSearch.trim() !== "";
      const tabMap: Record<WorkflowTab, "affecter" | "today" | "week" | "horizon"> = {
        A_AFFECTER: "affecter",
        TODAY: "today",
        WEEK: "week",
        ALL: "horizon",
      };

      const params: Record<string, string> = {
        listMode: usesClient ? "client" : "operational",
        date,
      };
      if (!usesClient) {
        params.operationalTab = tabMap[tab];
      }

      if (usesClient) {
        const pivot = new Date(`${date}T12:00:00`);
        const debut = new Date(pivot);
        debut.setMonth(debut.getMonth() - 5);
        const fin = new Date(pivot);
        fin.setMonth(fin.getMonth() + 3);
        params.dateDebut = debut.toISOString().slice(0, 10);
        params.dateFin = fin.toISOString().slice(0, 10);
      }

      if (zoneId.trim()) params.zoneId = zoneId.trim();
      if (operationalStatus.trim()) params.operationalStatus = operationalStatus.trim();
      if (includeBlocked) params.includeBlocked = "true";
      if (clientId.trim()) params.clientId = clientId.trim();
      if (clientSearch.trim()) params.clientSearch = clientSearch.trim();

      const [res, equipesRes] = await Promise.all([
        api.get<{
          items?: PassageRow[];
          affecterPreview?: PassageRow[];
          viewBadgeHint?: string;
          viewListSubtitle?: string;
          blockedPassagesHiddenCount?: number;
        }>("/passages", { params }),
        api.get("/equipes").catch(() => ({ data: [] as Array<{ id: number; nom: string }> })),
      ]);
      const rowsRaw = (res.data?.items ?? []) as PassageRow[];
      const affRaw = (res.data?.affecterPreview ?? []) as PassageRow[];
      const rows = usesClient ? rowsRaw : rowsRaw.filter((p) => !p.passageProcessed);
      const aff = usesClient ? affRaw : affRaw.filter((p) => !p.passageProcessed);
      const equipeList = Array.isArray(equipesRes.data) ? equipesRes.data : [];
      const nextMap: Record<number, string> = {};
      equipeList.forEach((e: { id: number; nom: string }) => {
        nextMap[e.id] = e.nom;
      });
      setEquipesMap(nextMap);
      setItems(rows);
      setAffecterItems(aff.slice(0, 30));
      setBlockedHiddenCount(
        typeof res.data?.blockedPassagesHiddenCount === "number" ? res.data.blockedPassagesHiddenCount : 0
      );
      if (res.data?.viewBadgeHint) setViewBadge(String(res.data.viewBadgeHint));
      if (res.data?.viewListSubtitle) setViewSubtitle(String(res.data.viewListSubtitle));
    } catch {
      toast.error("Impossible de charger les passages.");
      setItems([]);
      setAffecterItems([]);
      setBlockedHiddenCount(0);
    } finally {
      setLoading(false);
    }
  }, [clientId, clientSearch, date, includeBlocked, operationalStatus, tab, zoneId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onExternal = (): void => {
      void load();
    };
    window.addEventListener("adminExecutionPassagesRefresh", onExternal);
    return () => window.removeEventListener("adminExecutionPassagesRefresh", onExternal);
  }, [load]);

  useEffect(() => {
    Promise.all([
      api.get("/zones").catch(() => ({ data: [] })),
      api.get("/equipes").catch(() => ({ data: [] })),
      api.get("/charrois/vehicules").catch(() => ({ data: [] })),
    ]).then(([zRes, eRes, vRes]) => {
      setZones(Array.isArray(zRes.data) ? (zRes.data as ZoneOption[]) : []);
      setEquipes(Array.isArray(eRes.data) ? (eRes.data as TeamOption[]) : []);
      setVehicules(Array.isArray(vRes.data) ? (vRes.data as VehiculeOption[]) : []);
    });
  }, []);

  useEffect(() => {
    if (!selectedPassage || !tourneeModeRequiresRollingUnit(modeOperationnel)) {
      setModeAvailability(null);
      setModeAvailabilityLoading(false);
      setCreateResourceUnitId("");
      return;
    }

    let cancelled = false;
    setModeAvailabilityLoading(true);
    api
      .get("/tournees/operational-mode-availability", {
        params: {
          mode: modeOperationnel,
          date: (createDate || new Date(selectedPassage.datePrevue).toISOString().slice(0, 10)).slice(0, 10),
        },
      })
      .then((res) => {
        if (!cancelled) {
          const availability = res.data as OperationalModeAvailability;
          setModeAvailability(availability);
          setCreateResourceUnitId((current) =>
            availability.units.some((unit) => String(unit.id) === current) ? current : ""
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setModeAvailability({
            mode: modeOperationnel,
            required: true,
            total: 0,
            used: 0,
            available: 0,
            isAvailable: false,
            message: "Impossible de vérifier la disponibilité du matériel roulant.",
            units: [],
          });
        }
      })
      .finally(() => {
        if (!cancelled) setModeAvailabilityLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [createDate, modeOperationnel, selectedPassage]);

  const handleGenerate = async () => {
    const cid = Number(genContratId);
    if (!Number.isInteger(cid) || cid <= 0) {
      toast.warning("Indiquez un identifiant de contrat valide.");
      return;
    }
    try {
      const res = await api.post("/passages/generate-for-contrat", {
        contratId: cid,
        periodeDebut: new Date(genDebut).toISOString(),
        periodeFin: new Date(genFin).toISOString(),
      });
      toast.success(
        `Génération : ${res.data?.created?.length ?? 0} créé(s), ${res.data?.skipped ?? 0} ignoré(s) (doublons).`
      );
      void load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error ?? "Erreur génération.");
    }
  };

  const formatAssignApiError = (e: unknown, fallback: string): string => {
    const ax = e as {
      response?: {
        data?: {
          error?: string;
          message?: string;
          details?: { blockingPassage?: { datePrevue?: string; status?: string } };
        };
      };
    };
    const code = ax.response?.data?.error;
    if (code === "PASSAGE_SEQUENCE_BLOCKED") {
      const bp = ax.response?.data?.details?.blockingPassage;
      const d = bp?.datePrevue ? new Date(bp.datePrevue).toLocaleDateString("fr-FR") : "";
      const base = ax.response?.data?.message ?? "Impossible d'affecter ce passage : ordre séquentiel.";
      return d ? `${base} (passage bloquant : ${d}${bp?.status ? `, ${bp.status}` : ""})` : base;
    }
    return ax.response?.data?.message ?? ax.response?.data?.error ?? fallback;
  };

  const openAssignModal = async (p: PassageRow, forcedSequenceOverride?: string) => {
    const override = (forcedSequenceOverride ?? pendingSequenceOverrideReason ?? "").trim();
    const sequenceBlocked = isPassageSequenceBlocked(p);
    const provisionBlocked = isPassageProvisionBlocked(p);

    const blockedWithoutOverride = (sequenceBlocked || provisionBlocked) && !override;
    if (blockedWithoutOverride) {
      const parts: string[] = [];
      if (sequenceBlocked) {
        parts.push("l’ordre séquentiel des passages du contrat");
      }
      if (provisionBlocked) {
        parts.push("la provision du cycle (paiement avant prestation)");
      }
      toast.info(
        `Affectation bloquée : ${parts.join(" et ")}. Les profils ADMIN / SUPERVISEUR peuvent utiliser « Forcer affectation » avec un motif d’audit (10 caractères minimum).`
      );
      return;
    }

    if (!sequenceBlocked && !provisionBlocked) {
      setPendingSequenceOverrideReason(null);
    }

    if (forcedSequenceOverride != null && forcedSequenceOverride.trim().length >= 10) {
      setPendingSequenceOverrideReason(forcedSequenceOverride.trim());
    }

    setSelectedPassage(p);
    setCreateDate(new Date(p.datePrevue).toISOString().slice(0, 10));
    setCreateZoneId(p.zone?.id ? String(p.zone.id) : p.zoneId ? String(p.zoneId) : "");
    setShowZoneEditor(false);
    setCreateEquipeId(p.equipeId ? String(p.equipeId) : "");
    setCreateVehiculeId("");
    setModeOperationnel(TourneeOperationalMode.WALKING);
    setCreateResourceUnitId("");
    setCreateComment("");
    setSelectedCompatibleTourneeId(null);
    setAssignLoading(true);
    try {
      const res = await api.get("/tournees/compatibles", { params: { passageId: p.id } });
      setCompatibles((res.data?.tournees ?? []) as CompatibleTournee[]);
    } catch {
      toast.error("Impossible de charger les tournées compatibles.");
      setCompatibles([]);
    } finally {
      setAssignLoading(false);
    }
  };

  const addToExistingTournee = async (tourneeId: number) => {
    if (!selectedPassage) return;
    if (!isTeamAssignmentConfirmed(selectedPassage)) {
      toast.warning("Confirmez l'affectation équipe avant la mise en tournée.");
      return;
    }
    try {
      const o = pendingSequenceOverrideReason?.trim();
      const body: {
        passageId: number;
        sequenceOverrideReason?: string;
        provisionOverrideReason?: string;
      } = {
        passageId: selectedPassage.id,
      };
      if (o) {
        body.sequenceOverrideReason = o;
        body.provisionOverrideReason = o;
      }
      await api.post(`/tournees/${tourneeId}/passages`, body);
      toast.success("Passage ajouté à la tournée.");
      setSelectedPassage(null);
      setCompatibles([]);
      setPendingSequenceOverrideReason(null);
      void load();
    } catch (e: unknown) {
      toast.error(formatAssignApiError(e, "Ajout à la tournée impossible."));
    }
  };

  const createAndAssign = async () => {
    if (!selectedPassage) return;
    if (!isTeamAssignmentConfirmed(selectedPassage)) {
      toast.warning("Confirmez l'affectation équipe avant de créer une tournée.");
      return;
    }
    if (
      tourneeModeRequiresRollingUnit(modeOperationnel) &&
      (modeAvailabilityLoading || modeAvailability?.isAvailable === false || !createResourceUnitId)
    ) {
      toast.warning(
        modeAvailability?.message ?? "Sélectionnez une unité de matériel roulant avant de créer la tournée."
      );
      return;
    }
    try {
      const commentaire = createComment?.trim() || undefined;
      const o = pendingSequenceOverrideReason?.trim();
      const dateIso = `${(createDate || new Date(selectedPassage.datePrevue).toISOString().slice(0, 10)).slice(0, 10)}T00:00:00.000Z`;
      const vehiculeId =
        tourneeModeAllowsOptionalFleetVehicle(modeOperationnel) && createVehiculeId
          ? Number(createVehiculeId)
          : undefined;
      const res = await api.post("/tournees/create-with-passage", {
        passageId: selectedPassage.id,
        date: dateIso,
        zoneId: createZoneId ? Number(createZoneId) : undefined,
        equipeId: createEquipeId ? Number(createEquipeId) : undefined,
        vehiculeId,
        modeOperationnel,
        resourceUnitId: createResourceUnitId ? Number(createResourceUnitId) : undefined,
        commentaire,
        ...(o ? { sequenceOverrideReason: o, provisionOverrideReason: o } : {}),
      });
      const t = res.data?.tournee?.numero || `#${res.data?.tournee?.id ?? ""}`;
      toast.success(`Tournée ${t} créée et passage affecté.`);
      setSelectedPassage(null);
      setCompatibles([]);
      setPendingSequenceOverrideReason(null);
      void load();
    } catch (e: unknown) {
      toast.error(formatAssignApiError(e, "Création de tournée + affectation impossible."));
    }
  };

  const saveTeamReassignment = async () => {
    if (!selectedPassage) return;
    if (!createEquipeId) {
      toast.warning("Sélectionnez une équipe.");
      return;
    }
    try {
      await api.post(`/passages/${selectedPassage.id}/team/reassign`, {
        equipeId: Number(createEquipeId),
        reason: "Réaffectation équipe depuis /admin/passages",
      });
      toast.success("Équipe réaffectée, confirmation requise.");
      setSelectedPassage(null);
      setCompatibles([]);
      setPendingSequenceOverrideReason(null);
      void load();
    } catch (e: unknown) {
      toast.error(formatAssignApiError(e, "Réaffectation équipe impossible."));
    }
  };

  const saveTeamProposal = async () => {
    if (!selectedPassage) return;
    if (!createEquipeId) {
      toast.warning("Sélectionnez une équipe.");
      return;
    }
    try {
      await api.post(`/passages/${selectedPassage.id}/team/propose`, {
        equipeId: Number(createEquipeId),
        comment: "Équipe proposée depuis /admin/passages",
      });
      toast.success("Équipe proposée. Confirmez l'affectation avant la mise en tournée.");
      setSelectedPassage(null);
      setCompatibles([]);
      setPendingSequenceOverrideReason(null);
      void load();
    } catch (e: unknown) {
      toast.error(formatAssignApiError(e, "Proposition équipe impossible."));
    }
  };

  const confirmTeamAssignment = async (p: PassageRow) => {
    try {
      await api.post(`/passages/${p.id}/team/confirm`, {
        comment: "Affectation confirmée depuis /admin/passages",
      });
      toast.success("Affectation équipe confirmée.");
      setSelectedPassage((current) =>
        current?.id === p.id
          ? {
              ...current,
              teamAssignmentStatus: "CONFIRMED",
              teamAssignmentStatusResolved: "CONFIRMED",
              teamAssignment: {
                ...(current.teamAssignment ?? {}),
                status: "CONFIRMED",
                effectiveStatus: "CONFIRMED",
              },
            }
          : current
      );
      void load();
    } catch (e: unknown) {
      toast.error(formatAssignApiError(e, "Confirmation affectation impossible."));
    }
  };

  const clientLabel = (p: PassageRow): string => {
    const c = p.client;
    if (!c) return `Client #${p.clientId ?? "—"}`;
    return (c.nomEntreprise || [c.nom, c.prenom].filter(Boolean).join(" ")).trim() || `Client #${c.id}`;
  };

  const tabLabel = (t: WorkflowTab): string =>
    t === "A_AFFECTER" ? "À affecter" : t === "TODAY" ? "Aujourd'hui" : t === "WEEK" ? "Cette semaine" : "Tous";

  const formatPriority = (p: PassageRow): string => (p.isUrgent ? "URGENT" : "NORMALE");
  const dispatchStatusLabel = (p: PassageRow): string => {
    const assignment = teamAssignmentEffectiveStatus(p);
    if (p.passageProcessed) return "Terminé";
    if (assignment === "IN_PROGRESS") return "En exécution terrain";
    if (assignment === "TOURNEE") return "Affecté à tournée";
    if (assignment === "CONFIRMED") return "Équipe confirmée / À mettre en tournée";
    if (assignment === "PROPOSED") return "Équipe proposée / À confirmer";
    if (isPassageProvisionBlocked(p)) return "Bloqué provision";
    return "À affecter";
  };
  const primaryActionLabel = (p: PassageRow): string => {
    const assignment = teamAssignmentEffectiveStatus(p);
    if (assignment === "TOURNEE" || assignment === "IN_PROGRESS") return "Voir tournée";
    if (assignment === "CONFIRMED") return "Mettre en tournée";
    if (assignment === "PROPOSED") return "Confirmer affectation";
    return (p.adminActionLabel ?? "Assigner équipe").trim() || "Assigner équipe";
  };

  const operationalGroups = useMemo(() => {
    const groups = {
      toAssign: [] as PassageRow[],
      toConfirm: [] as PassageRow[],
      confirmedNeedsTournee: [] as PassageRow[],
      tourneeAssigned: [] as PassageRow[],
      inExecution: [] as PassageRow[],
      blocked: [] as PassageRow[],
      other: [] as PassageRow[],
    };
    for (const p of items) {
      if (p.passageProcessed) continue;
      const bucket = String(p.operationalBucket ?? "").toUpperCase();
      if (bucket === "BLOCKED_PROVISION" || isPassageProvisionBlocked(p) || isPassageSequenceBlocked(p)) groups.blocked.push(p);
      else if (bucket === "TO_CONFIRM") groups.toConfirm.push(p);
      else if (bucket === "CONFIRMED_NEEDS_TOURNEE") groups.confirmedNeedsTournee.push(p);
      else if (bucket === "TOURNEE_ASSIGNED") groups.tourneeAssigned.push(p);
      else if (bucket === "IN_EXECUTION") groups.inExecution.push(p);
      else if (bucket === "TO_ASSIGN") groups.toAssign.push(p);
      else groups.other.push(p);
    }
    return groups;
  }, [items]);

  function operationalBadgeClass(label: string): string {
    if (label.startsWith("EN RETARD")) return "bg-orange-600/25 text-orange-50 ring-orange-500/40";
    if (label.startsWith("URGENCE")) return "bg-rose-600/25 text-rose-50 ring-rose-500/40";
    if (label.startsWith("AUJOURD")) return "bg-sky-600/20 text-sky-50 ring-sky-500/35";
    if (label.startsWith("PREMI")) return "bg-violet-600/20 text-violet-50 ring-violet-500/35";
    if (label.startsWith("BLOQUÉ")) return "bg-slate-700/80 text-amber-100 ring-amber-600/30";
    return "bg-slate-700/60 text-slate-100 ring-slate-500/35";
  }

  function renderOperationalBadgeStrip(p: PassageRow): React.ReactNode {
    const badges = (p.operationalBadges ?? []).filter(Boolean);
    if (badges.length === 0) return null;
    return (
      <div className="mt-1 flex max-w-[16rem] flex-wrap gap-1">
        {badges.map((b) => (
          <span
            key={`${p.id}-${b}`}
            className={`inline-flex rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ring-1 ${operationalBadgeClass(b)}`}
          >
            {b}
          </span>
        ))}
      </div>
    );
  }

  function renderPassageTableRows(rows: PassageRow[]): React.ReactNode {
    return rows.map((p) => {
      const tourneeDetailId = p.tournee?.id ?? p.tourneeId ?? null;
      const apiRow = p as PassageApiItem;
      const lectureStatutRow = passageStatutLecture(apiRow);
      const actionLabel = primaryActionLabel(p);
      const assignmentStatus = teamAssignmentEffectiveStatus(p);
      return (
        <tr key={p.id} className="bg-slate-900/30">
          <td className="px-3 py-2 text-white">
            <div>{clientLabel(p)}</div>
            {clientFiltersActive && p.passageProcessed ? (
              <div className="mt-1">
                <span className="inline-flex rounded-md bg-emerald-900/45 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-100 ring-1 ring-emerald-600/40">
                  Déjà traité — voir historique
                </span>
              </div>
            ) : null}
            {renderOperationalBadgeStrip(p)}
          </td>
          <td className="px-3 py-2 text-slate-300">#{p.contratId}</td>
          <td className="px-3 py-2 text-slate-300">
            {new Date(p.datePrevue).toLocaleString("fr-FR")}
          </td>
          <td className="px-3 py-2 text-slate-400">{p.zone?.nom ?? "—"}</td>
          <td className="px-3 py-2 text-slate-400">
            {(p.equipeId && equipesMap[p.equipeId]) || "Aucune équipe"}
          </td>
          <td className="px-3 py-2">
            <div className="flex max-w-[14rem] flex-col gap-0.5">
              <span
                className={`inline-flex w-fit rounded-lg px-2 py-0.5 text-xs font-semibold ring-1 ${passageStatutLectureBadgeClass(apiRow)}`}
              >
                {lectureStatutRow.title}
              </span>
              {lectureStatutRow.subtitle ? (
                <span className="text-[10px] leading-snug text-rose-300/90">{lectureStatutRow.subtitle}</span>
              ) : null}
            </div>
          </td>
          <td className="px-3 py-2 text-slate-300">
            {p.passageType === "PREMIERE_INTERVENTION" ? "Première intervention" : "Standard"}
          </td>
          <td className="px-3 py-2 text-slate-300">{formatPriority(p)}</td>
          <td className="px-3 py-2 text-slate-300">
            {dispatchStatusLabel(p)}
          </td>
          <td className="px-3 py-2 text-slate-300">
            {p.tournee ? (
              <span>
                #{p.tournee.id} {p.tournee.numero ? `(${p.tournee.numero})` : ""}
              </span>
            ) : tourneeDetailId != null ? (
              <span>#{tourneeDetailId}</span>
            ) : (
              "—"
            )}
          </td>
          <td className="max-w-[14rem] px-3 py-2 text-slate-300">
            <div className="font-medium text-slate-200">
              {apiRow.billingLabel ?? (p.isFacturable ? "Prêt à facturer" : "—")}
            </div>
            {apiRow.billingReason ? (
              <div className="text-[10px] leading-snug text-slate-500">{apiRow.billingReason}</div>
            ) : null}
            {apiRow.billingNote ? (
              <div className="text-[10px] leading-snug text-amber-300/80">{apiRow.billingNote}</div>
            ) : null}
          </td>
          <td className="px-3 py-2 text-right">
            <div className="flex flex-col items-end gap-1.5">
              {tourneeDetailId == null && isPassageSequenceBlocked(p) ? (
                <span className="rounded-md bg-rose-950/45 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rose-200 ring-1 ring-rose-700/35">
                  Bloqué par passage antérieur
                </span>
              ) : null}
              {tourneeDetailId == null && isPassageProvisionBlocked(p) ? (
                <span
                  title={p.provisionBlockMessage ?? ""}
                  className="rounded-md bg-amber-950/50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-100 ring-1 ring-amber-700/40"
                >
                  Provision en attente
                </span>
              ) : null}
              <div className="flex flex-wrap justify-end gap-2">
                {tourneeDetailId == null ? (
                  <>
                    {p.passageProcessed ? (
                      <Link
                        to="/admin/passages/historique"
                        className="rounded-lg border border-emerald-600/40 px-2 py-1 text-xs text-emerald-300 hover:bg-emerald-950/30"
                      >
                        Voir historique
                      </Link>
                    ) : (
                      <>
                        <span
                          title={
                            passageSequenceTooltip(p) ??
                            (isPassageProvisionBlocked(p) ? (p.provisionBlockMessage ?? "") : "")
                          }
                          className="inline-flex"
                        >
                          {assignmentStatus === "PROPOSED" ? (
                            <button
                              type="button"
                              disabled={isPassageTerrainAssignBlocked(p)}
                              onClick={() => void confirmTeamAssignment(p)}
                              className="rounded-lg border border-amber-500/50 px-2 py-1 text-xs text-amber-100 hover:bg-amber-950/35 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              {actionLabel}
                            </button>
                          ) : (
                            <button
                              type="button"
                              disabled={isPassageTerrainAssignBlocked(p)}
                              onClick={() => void openAssignModal(p)}
                              className="rounded-lg border border-slate-600 px-2 py-1 text-xs text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              {actionLabel}
                            </button>
                          )}
                        </span>
                        {p.equipeId != null ? (
                          <button
                            type="button"
                            disabled={isPassageTerrainAssignBlocked(p)}
                            onClick={() => void openAssignModal(p)}
                            className="rounded-lg border border-violet-600/40 px-2 py-1 text-xs text-violet-200 hover:bg-violet-950/30 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            {assignmentStatus === "PROPOSED" ? "Changer équipe" : "Réaffecter équipe"}
                          </button>
                        ) : null}
                        {!p.passageProcessed && isPassageTerrainAssignBlocked(p) && canForceSequenceAssign ? (
                          <button
                            type="button"
                            onClick={() => {
                              setForceSequencePassage(p);
                              setForceSequenceReasonDraft("");
                            }}
                            className="rounded-lg border border-amber-600/50 px-2 py-1 text-xs text-amber-200 hover:bg-amber-950/40"
                          >
                            Forcer affectation
                          </button>
                        ) : null}
                      </>
                    )}
                  </>
                ) : (
                  <Link
                    to={`/admin/planificateur/tournees/${tourneeDetailId}`}
                    className="rounded-lg border border-emerald-600/40 px-2 py-1 text-xs text-emerald-300 hover:bg-emerald-950/30"
                  >
                    Voir tournée
                  </Link>
                )}
                <Link
                  to={`/admin/clients/${p.clientId ?? ""}?tab=operations`}
                  className="rounded-lg border border-sky-600/40 px-2 py-1 text-xs text-sky-300 hover:bg-sky-950/30"
                >
                  Voir client
                </Link>
              </div>
            </div>
          </td>
        </tr>
      );
    });
  }

  return (
    <div className="space-y-6 p-4 text-slate-100 md:p-6">
      <header className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-950/30 p-5 shadow-xl md:p-6">
        <p className="text-xs font-semibold uppercase tracking-widest text-emerald-400/90">Planification</p>
        <h1 className="mt-1 text-2xl font-bold text-white md:text-3xl">Passages (abonnement)</h1>
        <p className="mt-2 max-w-3xl text-sm text-slate-400">
          Unités de service issues des contrats. Les tournées regroupent ces passages pour l’exécution terrain ; le suivi
          historique se fait via les passages exécutés et validés.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link
            to="/admin/planificateur/tournees"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-800/80 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            Tournées
            <ChevronRight className="h-4 w-4" />
          </Link>
          <Link
            to="/admin/passages/historique"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800/60"
          >
            Historique passages
          </Link>
        </div>
      </header>

      <ClientDemandesAdminPanel
        crossLinkTo="/admin/interventions"
        crossLinkLabel="Voir aussi Exécution passages →"
      />

      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 md:p-5">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-white">
          <CalendarDays className="h-5 w-5 text-emerald-400" />
          Générer des passages pour un contrat
        </h2>
        <p className="mt-1 text-xs text-slate-500">
          Fréquence et nombre de passages mensuels : depuis le contrat (backend). Pas de doublon même jour pour un
          contrat.
        </p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Contrat ID
            <input
              type="number"
              value={genContratId}
              onChange={(e) => setGenContratId(e.target.value)}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
              placeholder="ex. 12"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Période début
            <input
              type="date"
              value={genDebut}
              onChange={(e) => setGenDebut(e.target.value)}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Période fin
            <input
              type="date"
              value={genFin}
              onChange={(e) => setGenFin(e.target.value)}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            />
          </label>
          <button
            type="button"
            onClick={() => void handleGenerate()}
            className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500"
          >
            Générer passages
          </button>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 md:p-5">
        <div className="mb-4 flex flex-wrap gap-2">
          {(["A_AFFECTER", "TODAY", "WEEK", "ALL"] as WorkflowTab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`rounded-xl px-3 py-2 text-sm ${
                tab === t
                  ? "bg-emerald-500/20 text-emerald-200"
                  : "border border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800"
              }`}
            >
              {tabLabel(t)}
            </button>
          ))}
        </div>

        <div className="mb-5 rounded-xl border border-amber-600/30 bg-amber-900/10 p-3">
          <p className="text-sm font-semibold text-amber-200">Passages à affecter</p>
          <p className="mt-1 text-xs text-amber-100/90">
            Priorité : retards et urgences, puis première intervention du jour, passages du jour sans tournée, fenêtre
            proche (tri serveur).
          </p>
          <div className="mt-3 overflow-x-auto rounded-lg border border-slate-800">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-slate-950 text-[10px] uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="px-2 py-2">Client</th>
                  <th className="px-2 py-2">Type</th>
                  <th className="px-2 py-2">Priorité</th>
                  <th className="px-2 py-2">Date prévue</th>
                  <th className="px-2 py-2">Zone</th>
                  <th className="px-2 py-2">Équipe</th>
                  <th className="px-2 py-2">Statut</th>
                  <th className="px-2 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {affecterItems.slice(0, 25).map((p) => {
                  const apiRow = p as PassageApiItem;
                  const prioLecture = passageStatutLecture(apiRow);
                  const actionLabelPrio = primaryActionLabel(p);
                  const tourneePrio = p.tournee?.id ?? p.tourneeId ?? null;
                  return (
                    <tr key={`prio-${p.id}`} className="bg-slate-900/40 text-slate-200">
                      <td className="px-2 py-2">
                        <div>{clientLabel(p)}</div>
                        {clientFiltersActive && p.passageProcessed ? (
                          <div className="mt-1">
                            <span className="inline-flex rounded-md bg-emerald-900/45 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-emerald-100 ring-1 ring-emerald-600/40">
                              Déjà traité — voir historique
                            </span>
                          </div>
                        ) : null}
                        {renderOperationalBadgeStrip(p)}
                      </td>
                      <td className="px-2 py-2">{p.passageType === "PREMIERE_INTERVENTION" ? "Première intervention" : "Standard"}</td>
                      <td className="px-2 py-2">{formatPriority(p)}</td>
                      <td className="px-2 py-2">{new Date(p.datePrevue).toLocaleString("fr-FR")}</td>
                      <td className="px-2 py-2">{p.zone?.nom ?? "—"}</td>
                      <td className="px-2 py-2">{(p.equipeId && equipesMap[p.equipeId]) || "Sans équipe"}</td>
                      <td className="px-2 py-2">
                        <div className="flex max-w-[13rem] flex-col gap-0.5">
                          <span
                            className={`inline-flex w-fit rounded px-2 py-0.5 font-semibold ring-1 ${passageStatutLectureBadgeClass(apiRow)}`}
                          >
                            {prioLecture.title}
                          </span>
                          {prioLecture.subtitle ? (
                            <span className="text-[10px] leading-tight text-rose-300/90">{prioLecture.subtitle}</span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-right">
                        <div className="flex flex-col items-end gap-1">
                          {isPassageSequenceBlocked(p) ? (
                            <span className="rounded bg-rose-950/50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-rose-100 ring-1 ring-rose-700/40">
                              Bloqué par passage antérieur
                            </span>
                          ) : null}
                          {isPassageProvisionBlocked(p) ? (
                            <span
                              title={p.provisionBlockMessage ?? ""}
                              className="rounded bg-amber-950/50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-100 ring-1 ring-amber-700/40"
                            >
                              Provision en attente
                            </span>
                          ) : null}
                          <div className="flex flex-wrap justify-end gap-2">
                            {tourneePrio == null ? (
                              p.passageProcessed ? (
                                <Link
                                  to="/admin/passages/historique"
                                  className="rounded border border-emerald-600/40 px-2 py-1 text-[10px] text-emerald-300 hover:bg-emerald-950/30"
                                >
                                  Voir historique
                                </Link>
                              ) : (
                                <>
                                  <span
                                    title={
                                      passageSequenceTooltip(p) ??
                                      (isPassageProvisionBlocked(p) ? (p.provisionBlockMessage ?? "") : "")
                                    }
                                    className="inline-flex"
                                  >
                                    <button
                                      type="button"
                                      disabled={isPassageTerrainAssignBlocked(p)}
                                      onClick={() => void openAssignModal(p)}
                                      className="rounded border border-slate-600 px-2 py-1 text-[10px] hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                      {actionLabelPrio}
                                    </button>
                                  </span>
                                {p.equipeId != null ? (
                                  <button
                                    type="button"
                                    disabled={isPassageTerrainAssignBlocked(p)}
                                    onClick={() => void openAssignModal(p)}
                                    className="rounded border border-violet-600/50 px-2 py-1 text-[10px] text-violet-200 hover:bg-violet-950/40 disabled:cursor-not-allowed disabled:opacity-40"
                                  >
                                    Réaffecter équipe
                                  </button>
                                ) : null}
                                </>
                              )
                            ) : (
                              <Link
                                to={`/admin/planificateur/tournees/${tourneePrio}`}
                                className="rounded border border-emerald-600/40 px-2 py-1 text-[10px] text-emerald-300 hover:bg-emerald-950/30"
                              >
                                Voir tournée
                              </Link>
                            )}
                            {!p.passageProcessed && isPassageTerrainAssignBlocked(p) && canForceSequenceAssign ? (
                              <button
                                type="button"
                                onClick={() => {
                                  setForceSequencePassage(p);
                                  setForceSequenceReasonDraft("");
                                }}
                                className="rounded border border-amber-600/50 px-2 py-1 text-[10px] text-amber-200 hover:bg-amber-950/40"
                              >
                                Forcer affectation
                              </button>
                            ) : null}
                            <Link
                              to={`/admin/clients/${p.clientId ?? ""}?tab=operations`}
                              className="rounded border border-sky-600/50 px-2 py-1 text-[10px] text-sky-300 hover:bg-sky-950/40"
                            >
                              Voir client
                            </Link>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {affecterItems.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-2 py-4 text-center text-slate-500">
                      Aucun passage prioritaire à affecter ou confirmer.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-white">Liste des passages</h2>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-emerald-500/40 bg-emerald-950/40 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-200">
                {viewBadge}
              </span>
              <p className="max-w-2xl text-xs leading-relaxed text-slate-500">{viewSubtitle}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-700 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Actualiser
          </button>
        </div>

        <div className="mt-4 flex flex-wrap gap-3">
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Date prévue (jour)
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Zone
            <select
              value={zoneId}
              onChange={(e) => setZoneId(e.target.value)}
              className="min-w-40 rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            >
              <option value="">Toutes zones</option>
              {zones.map((z) => (
                <option key={z.id} value={String(z.id)}>
                  {z.nom}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Statut
            <select
              value={operationalStatus}
              onChange={(e) => setOperationalStatus(e.target.value)}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            >
              <option value="">{OPERATIONAL_FILTER_ALL_LABEL_FR}</option>
              {OPERATIONAL_PASSAGE_STATUS_VALUES.map((s) => (
                <option key={s} value={s}>
                  {OPERATIONAL_STATUS_META[s].labelFr}
                </option>
              ))}
            </select>
          </label>
          <label className="flex cursor-pointer items-end gap-2 pb-0.5 text-xs text-slate-300">
            <input
              type="checkbox"
              checked={includeBlocked}
              onChange={(e) => setIncludeBlocked(e.target.checked)}
              className="h-4 w-4 rounded border-slate-600 bg-slate-950 text-emerald-500"
            />
            Afficher les passages bloqués
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Recherche client (nom / société)
            <input
              type="search"
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              className="min-w-[10rem] rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
              placeholder="ex. Tuluka"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-400">
            Client ID
            <input
              type="number"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className="w-28 rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
              placeholder="—"
            />
          </label>
        </div>

        {!includeBlocked && blockedHiddenCount > 0 ? (
          <button
            type="button"
            className="mt-2 cursor-pointer border-0 bg-transparent p-0 text-left text-xs text-sky-300 underline decoration-sky-500/60 underline-offset-2 hover:text-sky-200"
            onClick={() => setIncludeBlocked(true)}
          >
            {blockedHiddenCount} passage{blockedHiddenCount > 1 ? "s" : ""} bloqué
            {blockedHiddenCount > 1 ? "s" : ""} masqué{blockedHiddenCount > 1 ? "s" : ""}
            <span className="sr-only"> — afficher les passages bloqués</span>
          </button>
        ) : null}

        <div className="mt-6 overflow-x-auto rounded-xl border border-slate-800">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-800 bg-slate-950/80 text-[10px] font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-3">Client</th>
                <th className="px-3 py-3">Contrat</th>
                <th className="px-3 py-3">Date prévue</th>
                <th className="px-3 py-3">Zone</th>
                <th className="px-3 py-3">Équipe</th>
                <th className="px-3 py-3">Statut</th>
                <th className="px-3 py-3">Type</th>
                <th className="px-3 py-3">Priorité</th>
                <th className="px-3 py-3">Dispatch</th>
                <th className="px-3 py-3">Tournée</th>
                <th className="px-3 py-3">Facturation</th>
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/90">
              {items.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-3 py-10 text-center text-slate-500">
                    {loading ? "Chargement…" : "Aucun passage pour ces filtres."}
                  </td>
                </tr>
              ) : (
                <>
                  {operationalGroups.toAssign.length > 0 ? (
                    <>
                      <tr className="bg-slate-950/80">
                        <td
                          colSpan={12}
                          className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wide text-slate-200"
                        >
                          À affecter
                        </td>
                      </tr>
                      {renderPassageTableRows(operationalGroups.toAssign)}
                    </>
                  ) : null}
                  {operationalGroups.toConfirm.length > 0 ? (
                    <>
                      <tr className="bg-amber-950/20">
                        <td
                          colSpan={12}
                          className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wide text-amber-100"
                        >
                          À confirmer
                        </td>
                      </tr>
                      {renderPassageTableRows(operationalGroups.toConfirm)}
                    </>
                  ) : null}
                  {operationalGroups.confirmedNeedsTournee.length > 0 ? (
                    <>
                      <tr className="bg-emerald-950/20">
                        <td
                          colSpan={12}
                          className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wide text-emerald-100"
                        >
                          Équipe confirmée / À mettre en tournée
                        </td>
                      </tr>
                      {renderPassageTableRows(operationalGroups.confirmedNeedsTournee)}
                    </>
                  ) : null}
                  {operationalGroups.tourneeAssigned.length > 0 ? (
                    <>
                      <tr className="bg-sky-950/20">
                        <td
                          colSpan={12}
                          className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wide text-sky-100"
                        >
                          Affectés à tournée
                        </td>
                      </tr>
                      {renderPassageTableRows(operationalGroups.tourneeAssigned)}
                    </>
                  ) : null}
                  {operationalGroups.inExecution.length > 0 ? (
                    <>
                      <tr className="bg-violet-950/20">
                        <td
                          colSpan={12}
                          className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wide text-violet-100"
                        >
                          En exécution
                        </td>
                      </tr>
                      {renderPassageTableRows(operationalGroups.inExecution)}
                    </>
                  ) : null}
                  {operationalGroups.blocked.length > 0 ? (
                    <>
                      <tr className="bg-rose-950/25">
                        <td
                          colSpan={12}
                          className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wide text-rose-100"
                        >
                          Bloqués
                        </td>
                      </tr>
                      {renderPassageTableRows(operationalGroups.blocked)}
                    </>
                  ) : null}
                  {operationalGroups.other.length > 0 ? (
                    <>
                      <tr className="bg-slate-950/80">
                        <td
                          colSpan={12}
                          className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wide text-slate-300"
                        >
                          Autres passages opérationnels
                        </td>
                      </tr>
                      {renderPassageTableRows(operationalGroups.other)}
                    </>
                  ) : null}
                </>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <p className="text-center text-xs text-slate-500">
        Créer une tournée : menu « Tournées » ou{" "}
        <Link className="text-emerald-400 underline" to="/admin/planificateur/tournees">
          cette page
        </Link>
        .
      </p>

      {selectedPassage ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-[min(92vw,1400px)] max-w-[1400px] h-[min(88vh,980px)] overflow-hidden rounded-2xl border border-slate-800 bg-slate-900">
            <div className="h-full overflow-hidden p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-white">Assignation du passage</h3>
                <p className="text-xs text-slate-400">Affecter à une tournée existante ou créer une tournée dédiée.</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedPassage(null);
                  setPendingSequenceOverrideReason(null);
                }}
                className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
              >
                Fermer
              </button>
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-slate-700 bg-slate-950/70 px-3 py-2 text-xs text-slate-300">
              <p><span className="text-slate-400">Client:</span> {clientLabel(selectedPassage)}</p>
              <p><span className="text-slate-400">Zone:</span> {selectedPassage.zone?.nom ?? "—"}</p>
              <p><span className="text-slate-400">Équipe:</span> {(selectedPassage.equipeId && equipesMap[selectedPassage.equipeId]) || "Aucune équipe"}</p>
              <p><span className="text-slate-400">Priorité:</span> {formatPriority(selectedPassage)}</p>
              <p><span className="text-slate-400">Date:</span> {new Date(selectedPassage.datePrevue).toLocaleString("fr-FR")}</p>
            </div>

            {pendingSequenceOverrideReason ? (
              <div className="mt-3 rounded-lg border border-amber-600/50 bg-amber-950/30 px-3 py-2 text-xs text-amber-100">
                <p className="font-semibold text-amber-50">Affectation forcée (contournement ordre séquentiel)</p>
                <p className="mt-1 text-amber-200/95">
                  Un motif a été fourni pour l&apos;audit métier. Vérifiez qu&apos;il reste pertinent avant de valider.
                </p>
                <p className="mt-1 rounded border border-amber-800/40 bg-black/20 px-2 py-1 font-mono text-[11px] text-amber-100/90">
                  {pendingSequenceOverrideReason}
                </p>
              </div>
            ) : null}

            {!isTeamAssignmentConfirmed(selectedPassage) ? (
              <div className="mt-3 rounded-lg border border-amber-600/45 bg-amber-950/25 px-3 py-2 text-xs text-amber-100">
                <p className="font-semibold">Affectation équipe non confirmée</p>
                <p className="mt-1 text-amber-100/85">
                  Proposez une équipe puis confirmez l'affectation avant de créer ou rejoindre une tournée.
                </p>
                {selectedPassage.equipeId != null ? (
                  <button
                    type="button"
                    onClick={() => void confirmTeamAssignment(selectedPassage)}
                    className="mt-2 rounded border border-amber-500/50 px-3 py-1.5 text-[11px] font-medium text-amber-50 hover:bg-amber-950/45"
                  >
                    Confirmer l'affectation équipe
                  </button>
                ) : null}
              </div>
            ) : null}

            <div className="mt-3 grid h-[calc(100%-92px)] grid-cols-1 gap-4 lg:grid-cols-[65%_35%]">
              <section className="rounded-xl border border-slate-800 p-3 min-h-0 overflow-hidden">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h4 className="text-sm font-semibold text-white">Option A — Ajouter à une tournée existante</h4>
                  <button
                    type="button"
                    disabled={!selectedCompatibleTourneeId || !isTeamAssignmentConfirmed(selectedPassage)}
                    onClick={() =>
                      selectedCompatibleTourneeId
                        ? void addToExistingTournee(selectedCompatibleTourneeId)
                        : undefined
                    }
                    className="rounded border border-emerald-600/40 px-2 py-1 text-[11px] text-emerald-300 hover:bg-emerald-950/30 disabled:opacity-40"
                  >
                    Ajouter à la tournée sélectionnée
                  </button>
                </div>
                {assignLoading ? (
                  <p className="mt-2 text-xs text-slate-400">Chargement des tournées compatibles…</p>
                ) : compatibles.length === 0 ? (
                  <div className="mt-2 flex min-h-40 flex-col items-center justify-center rounded border border-slate-800 bg-slate-950/40 px-3 text-center">
                    <div className="mb-2 text-xl">🧭</div>
                    <p className="text-sm font-medium text-amber-100">
                      Aucune tournée disponible pour {selectedPassage.zone?.nom ?? "cette zone"} le{" "}
                      {new Date(selectedPassage.datePrevue).toLocaleDateString("fr-FR")}.
                    </p>
                    <button
                      type="button"
                      onClick={() => void createAndAssign()}
                      disabled={!isTeamAssignmentConfirmed(selectedPassage)}
                      className="mt-3 rounded border border-emerald-600/40 px-3 py-1.5 text-xs text-emerald-300 hover:bg-emerald-950/30"
                    >
                      Créer une nouvelle tournée
                    </button>
                  </div>
                ) : (
                  <div className="mt-2 h-[calc(100%-34px)] overflow-auto rounded border border-slate-800">
                    <table className="min-w-full text-left text-xs">
                      <thead className="bg-slate-950 text-[10px] uppercase text-slate-500">
                        <tr>
                          <th className="px-2 py-2">Tournée</th>
                          <th className="px-2 py-2">Date</th>
                          <th className="px-2 py-2">Zone</th>
                          <th className="px-2 py-2">Équipe</th>
                          <th className="px-2 py-2">Véhicule</th>
                          <th className="px-2 py-2">Passages</th>
                          <th className="px-2 py-2">Capacité restante</th>
                          <th className="px-2 py-2 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800">
                        {compatibles.map((t) => (
                          <tr
                            key={t.id}
                            onClick={() => setSelectedCompatibleTourneeId(t.id)}
                            className={`cursor-pointer transition hover:bg-slate-800/50 ${
                              selectedCompatibleTourneeId === t.id
                                ? "bg-emerald-950/30 ring-1 ring-emerald-500/40 shadow-[0_0_0_1px_rgba(16,185,129,0.15)]"
                                : ""
                            }`}
                          >
                            <td className="px-2 py-2 text-slate-200">#{t.id}</td>
                            <td className="px-2 py-2 text-slate-300">{new Date(t.date).toLocaleDateString("fr-FR")}</td>
                            <td className="px-2 py-2 text-slate-300">{t.zoneLabel ?? "—"}</td>
                            <td className="px-2 py-2 text-slate-300">{t.equipeLabel ?? "—"}</td>
                            <td className="px-2 py-2 text-slate-300">{t.vehiculeLabel ?? "—"}</td>
                            <td className="px-2 py-2 text-slate-300">{t.passagesCount}</td>
                            <td className="px-2 py-2 text-slate-300">
                              {t.capaciteRestante == null ? (
                                <span className="rounded-full bg-slate-700/60 px-2 py-0.5 text-[10px]">Capacité non définie</span>
                              ) : t.capaciteRestante > 0 ? (
                                <span className="rounded-full bg-emerald-600/20 px-2 py-0.5 text-[10px] text-emerald-200">
                                  {t.capaciteRestante} passages restants
                                </span>
                              ) : (
                                <span className="rounded-full bg-rose-600/20 px-2 py-0.5 text-[10px] text-rose-200">
                                  Capacité atteinte
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-2 text-right">
                              <button
                                type="button"
                                onClick={() => void addToExistingTournee(t.id)}
                                disabled={!isTeamAssignmentConfirmed(selectedPassage)}
                                className="rounded border border-emerald-600/40 px-2 py-1 text-[10px] text-emerald-300 hover:bg-emerald-950/30"
                              >
                                Ajouter
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <section className="rounded-xl border border-slate-800 p-3 min-h-0 overflow-auto">
                <h4 className="text-sm font-semibold text-white">Option B — Créer une nouvelle tournée avec ce passage</h4>
                <div className="mt-2 grid grid-cols-1 gap-2">
                  <label className="text-xs text-slate-400">
                    Date
                    <input type="date" value={createDate} onChange={(e) => setCreateDate(e.target.value)} className="mt-1 w-full rounded border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-white" />
                  </label>
                  <div className="rounded border border-slate-800 bg-slate-950/50 px-2 py-2 text-xs text-slate-300">
                    <p>
                      <span className="text-slate-400">Zone:</span>{" "}
                      {zones.find((z) => String(z.id) === createZoneId)?.nom || selectedPassage.zone?.nom || "—"}
                    </p>
                    <p className="mt-2">
                      <button
                        type="button"
                        onClick={() => setShowZoneEditor((v) => !v)}
                        className="text-[11px] text-sky-300 hover:underline"
                      >
                        Zone: {zones.find((z) => String(z.id) === createZoneId)?.nom || selectedPassage.zone?.nom || "—"} [modifier]
                      </button>
                    </p>
                  </div>
                  {showZoneEditor ? (
                    <label className="text-xs text-slate-400">
                      Zone
                      <select value={createZoneId} onChange={(e) => setCreateZoneId(e.target.value)} className="mt-1 w-full rounded border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-white">
                        <option value="">Aucune zone</option>
                        {zones.map((z) => <option key={z.id} value={String(z.id)}>{z.nom}</option>)}
                      </select>
                    </label>
                  ) : null}
                  <label className="text-xs text-slate-400">
                    Équipe assignée
                    <select value={createEquipeId} onChange={(e) => setCreateEquipeId(e.target.value)} className="mt-1 w-full rounded border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-white">
                      <option value="">Aucune équipe</option>
                      {equipes.map((eq) => <option key={eq.id} value={String(eq.id)}>{eq.nom}</option>)}
                    </select>
                  </label>
                  {selectedPassage.equipeId == null ? (
                    <button
                      type="button"
                      onClick={() => void saveTeamProposal()}
                      className="rounded border border-amber-500/45 bg-amber-950/25 px-3 py-2 text-xs font-medium text-amber-100 hover:bg-amber-950/45"
                    >
                      Proposer cette équipe
                    </button>
                  ) : !isTeamAssignmentConfirmed(selectedPassage) ? (
                    <button
                      type="button"
                      onClick={() => void saveTeamReassignment()}
                      className="rounded border border-amber-500/45 bg-amber-950/25 px-3 py-2 text-xs font-medium text-amber-100 hover:bg-amber-950/45"
                    >
                      Changer l'équipe proposée
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void saveTeamReassignment()}
                      className="rounded border border-violet-600/40 bg-violet-950/25 px-3 py-2 text-xs font-medium text-violet-200 hover:bg-violet-950/45"
                    >
                      Enregistrer la réaffectation équipe
                    </button>
                  )}
                  <label className="text-xs text-slate-400">
                    Mode opérationnel
                    <select
                      value={modeOperationnel}
                      onChange={(e) => setModeOperationnel(e.target.value as TourneeOperationalMode)}
                      className="mt-1 w-full rounded border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-white"
                    >
                      {TOURNEE_OPERATIONAL_MODE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <p className="text-[11px] text-slate-500">
                    Mode sélectionné : {tourneeOperationalModeLabelFr(modeOperationnel)}
                  </p>
                  {tourneeModeRequiresRollingUnit(modeOperationnel) ? (
                    <div
                      className={`rounded border px-2 py-2 text-[11px] ${
                        modeAvailabilityLoading
                          ? "border-slate-700 bg-slate-950/40 text-slate-300"
                          : modeAvailability?.isAvailable
                            ? "border-emerald-700/50 bg-emerald-950/20 text-emerald-200"
                            : "border-rose-700/50 bg-rose-950/25 text-rose-100"
                      }`}
                    >
                      {modeAvailabilityLoading
                        ? "Vérification disponibilité matériel roulant..."
                        : modeAvailability?.message ??
                          "Aucun matériel roulant disponible : ajoutez du stock ou libérez une tournée active."}
                    </div>
                  ) : null}
                  {tourneeModeRequiresRollingUnit(modeOperationnel) ? (
                    <label className="text-xs text-slate-400">
                      Matériel roulant à affecter *
                      <select
                        value={createResourceUnitId}
                        onChange={(e) => setCreateResourceUnitId(e.target.value)}
                        disabled={modeAvailabilityLoading || !modeAvailability?.isAvailable}
                        className="mt-1 w-full rounded border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-white disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <option value="">Sélectionnez une unité physique</option>
                        {(modeAvailability?.units ?? []).map((unit) => (
                          <option key={unit.id} value={String(unit.id)}>
                            {unit.label}
                            {unit.plateNumber ? ` · ${unit.plateNumber}` : ""}
                          </option>
                        ))}
                      </select>
                      <span className="mt-1 block text-[11px] text-slate-500">
                        Une unité déjà affectée à une tournée active ce jour n’est pas proposée.
                      </span>
                    </label>
                  ) : null}
                  {/* TODO: Future version should support multi-resource dispatch:
                      - multiple vehicles
                      - separate driver assignment
                      - operational equipment/material
                      - logistics optimization rules
                  */}
                  {tourneeModeAllowsOptionalFleetVehicle(modeOperationnel) ? (
                  <label className="text-xs text-slate-400">
                    Véhicule (optionnel)
                    <select value={createVehiculeId} onChange={(e) => setCreateVehiculeId(e.target.value)} className="mt-1 w-full rounded border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-white">
                      <option value="">Aucun véhicule rattaché</option>
                      {vehicules.map((v) => <option key={v.id} value={String(v.id)}>{v.immatriculation || `Véhicule #${v.id}`}</option>)}
                    </select>
                    {vehicules.length === 0 ? (
                      <span className="mt-1 block text-[11px] text-amber-200">Aucun véhicule disponible.</span>
                    ) : null}
                  </label>
                  ) : null}
                  <label className="text-xs text-slate-400">
                    Commentaire (optionnel)
                    <input type="text" value={createComment} onChange={(e) => setCreateComment(e.target.value)} className="mt-1 w-full rounded border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-white" />
                  </label>
                  <button
                    type="button"
                    onClick={() => void createAndAssign()}
                    disabled={
                      !isTeamAssignmentConfirmed(selectedPassage) ||
                      (tourneeModeRequiresRollingUnit(modeOperationnel) &&
                        (modeAvailabilityLoading || modeAvailability?.isAvailable === false || !createResourceUnitId))
                    }
                    className="mt-1 rounded border border-emerald-600/40 bg-emerald-950/30 px-3 py-2 text-xs font-medium text-emerald-200 hover:bg-emerald-950/50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Créer tournée et affecter passage
                  </button>
                  {!isTeamAssignmentConfirmed(selectedPassage) ? (
                    <p className="text-[11px] text-amber-200">
                      Bouton bloqué : confirmez d'abord l'affectation équipe, même pour une tournée à pied.
                    </p>
                  ) : tourneeModeRequiresRollingUnit(modeOperationnel) && !createResourceUnitId ? (
                    <p className="text-[11px] text-amber-200">
                      Bouton bloqué : sélectionnez l'unité physique de matériel roulant à affecter.
                    </p>
                  ) : null}
                </div>
              </section>
            </div>
            </div>
          </div>
        </div>
      ) : null}

      {forceSequencePassage ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/75 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-amber-700/50 bg-slate-900 p-5 shadow-2xl">
            <h3 className="text-lg font-semibold text-white">Forcer l&apos;affectation</h3>
            <p className="mt-2 text-sm text-amber-200/95">
              Vous outrepassez une règle métier (
              {[
                isPassageSequenceBlocked(forceSequencePassage) ? "ordre séquentiel des passages" : null,
                isPassageProvisionBlocked(forceSequencePassage) ? "provision du cycle (paiement avant prestation)" : null,
              ]
                .filter(Boolean)
                .join(" ; ") || "contrainte terrain"}
              ). Cette action est journalisée.
            </p>
            <label className="mt-4 block text-xs text-slate-400">
              Motif obligatoire (min. 10 caractères)
              <textarea
                value={forceSequenceReasonDraft}
                onChange={(e) => setForceSequenceReasonDraft(e.target.value)}
                className="mt-1 w-full min-h-[5rem] rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
                placeholder="Expliquez la justification métier…"
              />
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setForceSequencePassage(null);
                  setForceSequenceReasonDraft("");
                }}
                className="rounded-lg border border-slate-600 px-3 py-2 text-xs text-slate-300 hover:bg-slate-800"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => {
                  const t = forceSequenceReasonDraft.trim();
                  if (t.length < 10) {
                    toast.warning("Motif obligatoire (10 caractères minimum).");
                    return;
                  }
                  const passageToOpen = forceSequencePassage;
                  setForceSequencePassage(null);
                  setForceSequenceReasonDraft("");
                  void openAssignModal(passageToOpen, t);
                }}
                className="rounded-lg bg-amber-600 px-3 py-2 text-xs font-semibold text-slate-950 hover:bg-amber-500"
              >
                Continuer vers l&apos;affectation
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
